/****************
 * NUMBERS AND DATES IN THE USER'S LANGUAGE. 2026-09-26.
 *
 * Guillaume, after reading the app in French: dates read "Sep 26, 17:39" and
 * money "$-3.628" whatever the language. Everything that formats a number or a
 * date for the screen goes through here, so it follows the language chosen in
 * the header (not the browser's own locale, which toLocaleString(undefined)
 * used). English output is exactly what it was.
 *
 * Arabic uses Latin digits (ar-u-nu-latn): trading screens in Arabic show
 * 0-9, and prices must line up with the exchange's.
 ****************/
import { format as dfFormat } from 'date-fns';
import { enUS, es, fr, ptBR, ru, zhCN, hi, tr, vi, ko, ar } from 'date-fns/locale';
import i18n from '@/i18n';

const INTL: Record<string, string> = {
  en: 'en-US', es: 'es-419', fr: 'fr-FR', pt: 'pt-BR', ru: 'ru-RU', zh: 'zh-CN',
  hi: 'hi-IN', tr: 'tr-TR', vi: 'vi-VN', ko: 'ko-KR', ar: 'ar-u-nu-latn',
};
const DATE_FNS = { en: enUS, es, fr, pt: ptBR, ru, zh: zhCN, hi, tr, vi, ko, ar } as const;

/** The app's language code ("en", "fr"...). */
export function appLanguage(): string {
  return (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
}
/**
 * The BCP 47 locale for NUMBERS (Intl.NumberFormat, toLocaleString).
 *
 * Arabic numbers are written the English way, "+$73.73". Guillaume's first
 * look at Arabic, 2026-09-26: Intl's Arabic money is "73.73 US$", which the
 * page showed as "$US 73.73+" once the sentence around it ran right to left.
 * Arabic trading screens show amounts left to right with Latin digits.
 */
export function intlLocale(): string {
  return appLanguage() === 'ar' ? 'en-US' : INTL[appLanguage()] ?? 'en-US';
}
/** The BCP 47 locale for DATES: Arabic month and day names, Latin digits. */
export function dateLocale(): string {
  return INTL[appLanguage()] ?? 'en-US';
}

const RTL = new Set(['ar']);
/**
 * A number, amount or percentage kept in one left-to-right piece inside a
 * right-to-left sentence. Without it the bidi algorithm moves the "+", "$" and
 * "%" to the other side ("73.73+", "22%)"). The marks (U+2066/U+2069) are
 * invisible; outside right-to-left languages the text is returned as is.
 */
export function ltr(text: string): string {
  return RTL.has(appLanguage()) ? `\u2066${text}\u2069` : text;
}
/*
 * A bare number needs no marks: digits already run left to right, and "16.0x"
 * or "$" + "5.6" stay together. Marks there would split "16.0" from its "x".
 * Only a leading minus is moved by the algorithm ("-3.5" -> "3.5-").
 */
function signed(text: string): string {
  return /^[-+\u2212]/.test(text) ? ltr(text) : text;
}
/**
 * "+" in front of an already formatted amount, the two kept together. The
 * screens wrote `{v >= 0 ? '+' : ''}{fmtUsd(v)}`: fine left to right, but in
 * Arabic the lone "+" went to the other end ("73.73+"). Same output in English.
 */
export function withSign(plus: boolean, text: string): string {
  return ltr((plus ? '+' : '') + stripLtr(text));
}
/** The text without ltr()'s marks, for code that reads a formatted number back. */
export function stripLtr(text: string): string {
  return text.replace(/[\u2066\u2069]/g, '');
}

/* ── Dates ─────────────────────────────────────────────────────────────── */

/*
 * The patterns the app writes, as Intl options, so other languages get their
 * own order ("26 sept., 17:39", "9月26日 17:39") instead of English order with
 * translated month names. English keeps date-fns and the exact pattern.
 */
const H: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };
const PATTERNS: Record<string, Intl.DateTimeFormatOptions> = {
  'MMM d, HH:mm': { month: 'short', day: 'numeric', ...H },
  'MMM d HH:mm': { month: 'short', day: 'numeric', ...H },
  'MMM dd, yyyy HH:mm': { month: 'short', day: 'numeric', year: 'numeric', ...H },
  'MMM d, yyyy HH:mm': { month: 'short', day: 'numeric', year: 'numeric', ...H },
  'MMM dd, yyyy HH:mm:ss': { month: 'short', day: 'numeric', year: 'numeric', ...H, second: '2-digit' },
  'MMM d': { month: 'short', day: 'numeric' },
  'MMM dd': { month: 'short', day: 'numeric' },
  'LLL dd, y': { month: 'short', day: 'numeric', year: 'numeric' },
  'MMM d, yyyy': { month: 'short', day: 'numeric', year: 'numeric' },
  'MMM dd, yyyy': { month: 'short', day: 'numeric', year: 'numeric' },
  'HH:mm': { ...H },
  'HH:mm:ss': { ...H, second: '2-digit' },
  'MM/dd/yy HH:mm': { year: '2-digit', month: '2-digit', day: '2-digit', ...H },
  'MM/dd HH:mm': { month: '2-digit', day: '2-digit', ...H },
  'EEEE, MMMM d': { weekday: 'long', month: 'long', day: 'numeric' },
  'MMMM dd, yyyy - HH:mm': { month: 'long', day: 'numeric', year: 'numeric', ...H },
};

/**
 * date-fns `format`, in the app's language. A drop-in replacement: files
 * import `format` from here instead of from 'date-fns'.
 */
export function format(date: Date | number | string, pattern: string, options?: Parameters<typeof dfFormat>[2]): string {
  const d = date instanceof Date ? date : new Date(date);
  const lang = appLanguage();
  if (lang !== 'en' && PATTERNS[pattern]) {
    return new Intl.DateTimeFormat(dateLocale(), PATTERNS[pattern]).format(d);
  }
  return dfFormat(d, pattern, { locale: DATE_FNS[lang as keyof typeof DATE_FNS] ?? enUS, ...options });
}

/****************
 * A DATE AND TIME IN A TABLE, THE SAME EVERYWHERE. 2026-10-01.
 *
 * Guillaume: "I would like the date format to be the same everywhere and have
 * like Month, Date on top and Year and time below and centered." The sessions
 * table wrapped "Sep 28, 2026 15:30" wherever the column happened to break it
 * ("Sep 28, / 2026 15:30" on one row, "Sep 11, 2026 / 13:05" on the next), and
 * every table had its own pattern.
 *
 * Two lines, always broken at the same place: ["Sep 28", "2026 15:30"]. The
 * first line is the app's 'MMM d' in the chosen language ("28 sept."), the
 * time is the device's local time, as every date here already was. Rendered by
 * <DateTimeCell>. null when there is no date to show.
 ****************/
export function dateTimeLines(
  date: Date | number | string | null | undefined,
  opts: { seconds?: boolean } = {},
): [string, string] | null {
  if (date == null || date === '') return null;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return [format(d, 'MMM d'), `${d.getFullYear()} ${format(d, opts.seconds ? 'HH:mm:ss' : 'HH:mm')}`];
}

/* ── Numbers ───────────────────────────────────────────────────────────── */

/** A number with grouping, e.g. 1,234.5 / 1 234,5. */
export function fmtNumber(v: number, maxDigits = 2, minDigits = 0, group = true): string {
  return signed(v.toLocaleString(intlLocale(), { minimumFractionDigits: minDigits, maximumFractionDigits: maxDigits, useGrouping: group }));
}

/** Like v.toFixed(n) for DISPLAY: exactly n decimals, the language's decimal mark, no grouping. */
export function fmtFixed(v: number, digits: number): string {
  if (!Number.isFinite(v)) return String(v);
  return signed(v.toLocaleString(intlLocale(), { minimumFractionDigits: digits, maximumFractionDigits: digits, useGrouping: false }));
}

/**
 * Dollars: "-$3.63" in English, "-3,63 $" in French. `sign` adds "+" to
 * positive amounts (for PnL).
 */
export function fmtUsd(v: number, digits = 2, opts: { sign?: boolean; minDigits?: number; group?: boolean } = {}): string {
  if (!Number.isFinite(v)) return '—';
  return ltr(new Intl.NumberFormat(intlLocale(), {
    style: 'currency', currency: 'USD', currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: opts.minDigits ?? digits, maximumFractionDigits: digits,
    signDisplay: opts.sign ? 'exceptZero' : 'auto',
    useGrouping: opts.group ?? true,
  }).format(v));
}

/**
 * A percentage given in percent units (12.5 -> "12.5%" / "12,5 %"). Ungrouped
 * by default, like the toFixed it replaces: an ROI of 1010.6% stays "1010.6%".
 */
export function fmtPct(v: number, digits = 2, opts: { sign?: boolean; group?: boolean } = {}): string {
  if (!Number.isFinite(v)) return '—';
  return ltr(new Intl.NumberFormat(intlLocale(), {
    style: 'percent', minimumFractionDigits: digits, maximumFractionDigits: digits,
    signDisplay: opts.sign ? 'exceptZero' : 'auto',
    useGrouping: opts.group ?? false,
  }).format(v / 100));
}
